import { FullScreenLoading } from '@civilgrid/ui/components';
import Dinero from 'dinero.js';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import type { ChartPoint, TimeRange } from '~/common/charts/types';
import { Dropdown } from '~/components2/Dropdown';
import Icon from '~/components2/Icon';
import rootStore from '~/store';
import SimpleBarChart from '../../components2/dashboard/charts/SimpleBarChart';
import { MetricCard } from '../../components2/dashboard/widgets/MetricCard';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';
import orgDashboardStore from './store';

const formatHours = (hours: number): string => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(hours);
};

const formatCurrency = (amount: number): string => {
  // For millions
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  }
  // For thousands
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(2)}K`;
  }
  // For regular numbers
  return Dinero({ amount: Math.round(amount * 100) }).toFormat('$0,0');
};

export default function OrgSummary() {
  const { loading, error, chartData, timeRange } = useSnapshot(orgDashboardStore);

  const { currentOrganizationDetail } = useSnapshot(rootStore);

  useEffect(() => {
    if (!currentOrganizationDetail.isLoading && currentOrganizationDetail.id) {
      orgDashboardStore.fetchChartData(currentOrganizationDetail.id);
      return () => {
        orgDashboardStore.reset();
      };
    }
  }, [currentOrganizationDetail]);

  if (currentOrganizationDetail.isLoading) {
    return <div>Loading organization...</div>;
  }

  if (loading) return <FullScreenLoading />;
  if (error) return <div>Error: {error}</div>;
  if (!chartData) return null;

  const timeRanges: Array<{ value: TimeRange; label: string }> = [
    {
      value: '3months',
      label: 'Last 3 Months',
    },
    {
      value: '6months',
      label: 'Last 6 Months',
    },
    {
      value: '9months',
      label: 'Last 9 Months',
    },
    {
      value: '1year',
      label: 'Last Year',
    },
    {
      value: 'all',
      label: 'All Time',
    },
  ];

  const timeRangeOptions = timeRanges.map((range) => ({
    type: 'simple' as const,
    label: range.label,
    value: range.value,
    onClick: () => orgDashboardStore.updateTimeRange(currentOrganizationDetail.id, range.value),
  }));

  const currentTimeRangeLabel = timeRanges.find((range) => range.value === timeRange)?.label || 'Custom';

  const projectMetricsArray = [...chartData.projectMetrics.data] as ChartPoint[];

  const dataRequestMetricsArray = [...chartData.dataRequestMetrics.data] as ChartPoint[];

  if (loading) {
    return <FullScreenLoading />;
  }

  // it doesn't make sense to make this one hour per request because it's the same as the total requests
  const hoursSaved = chartData.dataRequestMetrics.total! * 1.71;
  const formattedHoursSaved = formatHours(hoursSaved);
  const dollarsSaved = formatCurrency(hoursSaved * 55);

  return (
    <ProtectedRouteLayout includeOrgProjectSwitcher={false} includeSearch={false}>
      <div className="flex items-center justify-center">
        <div className="flex h-screen flex-col overflow-hidden ">
          <div className="flex h-[8vh] items-center justify-between py-1">
            <div className="text-xl font-normal">{currentOrganizationDetail.name} - Summary</div>

            <Dropdown
              items={timeRangeOptions}
              triggerElement={
                <div
                  data-testid="org-summary-time-range-trigger"
                  className="flex cursor-pointer flex-row items-center justify-center space-x-2 rounded-md border bg-white px-2 py-1 hover:bg-gray-50"
                >
                  <Icon icon="calendar" />
                  <div>{currentTimeRangeLabel}</div>
                </div>
              }
            />
          </div>

          <div className="flex-1 overflow-y-auto pb-4">
            <div className="grid grid-cols-4 gap-4 gap-y-10">
              {/* First Section */}
              <div className="flex h-[40vh] flex-col gap-2">
                <MetricCard
                  title="Total Projects Created"
                  value={chartData.projectMetrics.total!}
                  // timeRangeLabel={currentTimeRangeLabel}
                  iconName="activity"
                />
                <MetricCard
                  title="Total Project Area (acres)"
                  value={chartData.projectMetrics.totalProjectArea}
                  // timeRangeLabel={currentTimeRangeLabel}
                  iconName="activity"
                />
                <MetricCard
                  title="Total Linear Miles"
                  value={chartData.projectMetrics.totalLinearMiles}
                  // timeRangeLabel={currentTimeRangeLabel}
                  iconName="activity"
                />
              </div>

              <div className="col-span-3 h-[40vh]">
                <SimpleBarChart
                  data={projectMetricsArray}
                  title={`Projects Created`}
                  timeRangeLabel={currentTimeRangeLabel}
                  yAxisLabel="Projects"
                />
              </div>

              {/* Second Section */}
              <div className="flex h-[40vh] flex-col gap-2">
                <MetricCard
                  title="Total Data Requests"
                  value={chartData.dataRequestMetrics.total!}
                  // timeRangeLabel={currentTimeRangeLabel}
                  iconName="barChart"
                />

                <MetricCard
                  title="Hours Saved"
                  value={formattedHoursSaved}
                  // timeRangeLabel={currentTimeRangeLabel}
                  iconName="clock"
                />

                <MetricCard
                  title="Dollars Saved"
                  value={dollarsSaved}
                  // timeRangeLabel={currentTimeRangeLabel}
                  iconName="currency"
                />
              </div>

              <div className="col-span-3 h-[40vh]">
                <SimpleBarChart
                  data={dataRequestMetricsArray}
                  title={`Data Requests Created`}
                  timeRangeLabel={currentTimeRangeLabel}
                  yAxisLabel="Requests"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRouteLayout>
  );
}
